/**
 * TypingIndicator — presentational strip rendered between the message
 * list and the composer. Shows "<who> is typing" with three animated
 * dots, or collapses to nothing when nobody is typing (no layout
 * shift).
 *
 * Display rules:
 *   role='agent'    → "<name | 'Agent'> is typing"
 *   role='customer' → "Customer is typing" (we don't capture customer
 *                     name in MVP — see the v0.4 audit doc for the
 *                     deferred customerName field)
 *
 * Accessibility:
 *   - aria-live='polite' so screen readers announce changes without
 *     interrupting the user's flow.
 *   - aria-atomic so the whole label is read as one unit, not just
 *     the diff.
 */
import type { RemoteTypingState } from './useTypingIndicator'

export function TypingIndicator({ remote }: { remote: RemoteTypingState }) {
  // Always render a fixed-height container so the message list doesn't
  // jump when the indicator appears / disappears. The dots are
  // visibility-toggled within it.
  return (
    <div
      className="px-4 sm:px-6 h-6 flex items-center text-xs text-slate-500 italic"
      aria-live="polite"
      aria-atomic="true"
    >
      {remote.isTyping && (
        <span className="inline-flex items-center gap-1.5">
          <span>{labelFor(remote)} is typing</span>
          <Dots />
        </span>
      )}
    </div>
  )
}

function labelFor(remote: RemoteTypingState): string {
  if (remote.role === 'customer') return 'Customer'
  if (remote.role === 'agent') return remote.name?.trim() || 'Agent'
  return 'Someone'
}

/** Three dots that pulse in sequence — pure CSS, no JS animation cost. */
function Dots() {
  return (
    <span className="inline-flex gap-0.5" aria-hidden>
      <Dot delay="0ms" />
      <Dot delay="150ms" />
      <Dot delay="300ms" />
    </span>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block w-1 h-1 rounded-full bg-slate-400"
      style={{
        animation: 'sb-typing-dot 1.2s infinite ease-in-out',
        animationDelay: delay,
      }}
    />
  )
}
